
import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useSelector } from "react-redux";
import { Mui } from "@osu/react-ui";
import { isBefore, parse, subDays } from "date-fns";
import { find, findIndex, invert } from "lodash";
import Questions from "../../../Common/components/Questions.js";
import { studentQuestions } from "../../../Forms/data/ApplicationToGraduate.js";
import { ACTION_STATUS, APPROVAL_STATUS, BOOL, EVENT_SAVE_APPROVAL_FORM } from "../../../util/constants.js";

export default function ApplicationToGraduate(props = {}) {
    const { form, formActionStatus, initialized, save, setInitialized, user } = props;
    const { osuid } = user;
    const [questionValues, questionDispatch] = useReducer(questionReducer, {});
    const terms = useSelector((state) => ((state?.dropdowns?.term?.options ?? [])));
    const [questions, setQuestions] = useState([]);

    const buildApprovalForm = (q, qv) => {
        const approvalForm = {};
        for(const question of q) {
            const { dataField, id } = question;
            const dataFields = dataField.split(".");
            const formField = (dataFields.length > 1 ? dataFields[dataFields.length - 1] : dataField);
            const questionValue = qv[id].value;
            const value = (questionValue?.value ?? questionValue);
            approvalForm[formField] = value;

            // build nested fields
            if(question?.choices?.length) {
                let selectedChoice;
                let branches = [];
                for(const choice of question.choices) {
                    if(choice.value === value) selectedChoice = choice;
                    if(choice?.branch) branches.push(choice.branch);
                }
                if(selectedChoice && selectedChoice?.branch) {
                    const branchChoice = find(question.choices, ["id", selectedChoice.branch]);
                    if(branchChoice) {
                        const branchValue = qv[branchChoice.id].value;
                        if(Array.isArray(branchValue)) {
                            const values = [];
                            for(const bv of branchValue) {
                                const { dataFieldMapping = {} } = bv;
                                const v = {};
                                for(const questionId of Object.keys(dataFieldMapping)) {
                                    const dataField = dataFieldMapping[questionId];
                                    const value = bv[questionId];
                                    v[dataField] = value;
                                }
                                values.push(v);
                            }
                            approvalForm[branchChoice.dataField] = values;
                        } else {
                            approvalForm[branchChoice.dataField] = branchValue;
                        }
                    }
                }
                // clear fields with unused branches
                if(branches.length > 0) {
                    for(const choice of question.choices) {
                        if(branches.includes(choice.id)) {
                            if(!approvalForm[choice.dataField]) approvalForm[choice.dataField] = null;
                        }
                    }
                }
            }
        }
        
        return approvalForm;
    };
    const buildQuestionValues = useCallback((questions) => {
        return questions.reduce((acc, question) => {
            const field = question.dataField;
            const fields = field.split(".");
            let value = null;
            if(fields.length > 1) {
                for(const f of fields) {
                    value = (value === null ? form[f] : value[f]);
                }
            } else {
                value = form[field];
            }
            acc[question.id] = { value };
            
            // build nested fields
            if(question?.choices?.length) {
                const selectedChoice = find(question.choices, ["value", value]);
                if(selectedChoice && selectedChoice?.branch) {
                    const branchChoice = find(question.choices, ["id", selectedChoice.branch]);
                    if(branchChoice) {
                        const branchValue = form[branchChoice.dataField];
                        if(Array.isArray(branchValue)) {
                            const dataFieldMapping = {};
                            for(const branchQuestion of branchChoice.questions) {
                                dataFieldMapping[branchQuestion.id] = branchQuestion.dataField;
                            }
                            const questionIdByDataFieldMap = invert(dataFieldMapping);
                            const values = [];
                            branchValue.forEach((bv, bvIndex) => {
                                const v = { id: bvIndex, dataFieldMapping };
                                for(const dataField of Object.keys(bv)) {
                                    const questionId = questionIdByDataFieldMap[dataField];
                                    v[questionId] = bv[dataField];
                                }
                                values.push(v);
                            });
                            acc[branchChoice.id] = { value: values };
                        } else {
                            acc[branchChoice.id] = { value: branchValue };
                        }
                    }
                }
            }

            return acc;
        }, {});
    }, [form]);
    const onSave = useCallback(() => {
        const approvalForm = buildApprovalForm(questions, questionValues);
        save(approvalForm);
    }, [save, questions, questionValues]);

    // initialize approval form (if necessary)
    useEffect(() => {
        if(initialized === false && form && form.status === APPROVAL_STATUS.APPROVED) {
            let approvalFormQuestions = null;
            // Student
            if(osuid === form.studentid) {
                const termIndex = findIndex(terms, ["value", form.termCode]);
                if(termIndex !== -1) {
                    const term = (form.endOfSemesterOption === BOOL.YES ? terms?.[termIndex + 1] : terms[termIndex]); // selected term or next term for EOS option
                    if(term && term?.endDate) {
                        const termEndDate = parse(term.endDate, "yyyy-MM-dd", new Date());
                        termEndDate.setHours(23,59,59,999); // end of day
                        const deadline = subDays(termEndDate, 3); // deadline is three days before term end date
                        const now = new Date();
                        if(isBefore(now, deadline)) approvalFormQuestions = studentQuestions;
                    }
                }
            }
            if(approvalFormQuestions) {
                setQuestions(approvalFormQuestions);
                questionDispatch({ type: "INITIALIZE", payload: buildQuestionValues(approvalFormQuestions) });
            }
        }
    }, [buildQuestionValues, form, initialized, osuid, terms]);

    // add listener for save approval form event
    useEffect(() => {
        document.addEventListener(EVENT_SAVE_APPROVAL_FORM, onSave);
        return () => {
            document.removeEventListener(EVENT_SAVE_APPROVAL_FORM, onSave);
        };
    }, [onSave]);

    if(questions.length > 0) {
        if(initialized === false) setInitialized(true);
        return (
            <Mui.Box display={(formActionStatus === ACTION_STATUS.LOADING ? "none" : "block")} paddingTop="1rem">
                <Mui.Typography component="h3" variant="h4" className="sr-only">Student Form</Mui.Typography>
                <Mui.Box>
                    <Questions questions={questions} dispatchStepState={questionDispatch} values={questionValues} />
                </Mui.Box>
            </Mui.Box>
        );
    }

    return null;
}

function questionReducer(state, action) {
    const { payload, type } = action;
    switch(type) {
        case "INITIALIZE":
            return payload;
        case "UPDATE": {
            const { error = null, nested = false, value } = payload;
            const field = (nested === true ? payload.nestedId : payload.field);
            const fieldState = state[field];
            const newState = { ...state, [field]: { ...fieldState, error, value: (value?.value ?? value) } };
            return newState;
            //return { ...state, [field]: { ...fieldState, error, value } };
        }
        default:
            return state
    }
}